import { useEffect, useState } from 'react';
import { Box, Paper, IconButton, styled, Grid2 as Grid, Alert } from '@mui/material';
import Canvas from '../../components/esl/canvas/Canvas';
import Sidebar from '../../components/esl/sidebar/Sidebar';
import { useLocation, useNavigate } from 'react-router-dom';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import IEslTemplate from '../../models/esl/eslTemplate';
import IEslTemplateItem from '../../models/esl/eslTemplateElement';
import eslTemplateService from '../../services/esl/EslTemplateService';
import { useTranslation } from 'react-i18next';
import { EEslTemplateElementType } from '../../models/enums/eslTemplateType';
import EditOnly from '../../components/permissions/EditOnly';
import Progress from '../../components/progress/Progress';
import { EslPreviewModeProvider } from '../../components/esl/EslPreviewModeContext';
import EslPreviewModeToggle from '../../components/esl/EslPreviewModeToggle';

const Toolbar = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1%',
  width: '100%',
}));

const TemplateEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['eslTemplate', 'common']);

  const id = (location.state as IEslTemplate)?.id;
  const [template, setTemplate] = useState<IEslTemplate>();
  const [cachedTemplate, setCachedTemplate] = useState<IEslTemplate>();
  const [selectedElementId, setSelectedElementId] = useState<number>(0);
  const [isTemplateModified, setIsTemplateModified] = useState(true);
  const [isErrorDisplayed, setIsErrorDisplayed] = useState(false);
  const [imageUrls, setImageUrls] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!id) return;

    getTemplate(id);
  }, [id]);

  const getTemplate = async (id: string) => {
    setIsLoading(true);
    const template = await eslTemplateService.GetEslTemplateById(id);
    if (!template) return;

    await getImageUrls(template.elements);

    setTemplate(template);
    setCachedTemplate(template);
    setIsLoading(false);
  }

  const getImageUrls = async (elements: IEslTemplateItem[]) => {
    const imageElements = elements.filter((el) => el.type === EEslTemplateElementType.Image && el.imageSrc);
    if (!imageElements.length) return elements;

    const imageKeys = imageElements.map((el) => el.imageSrc!);
    const imageUrls = await eslTemplateService.GetTemplateImageUrls(imageKeys);
    if (!imageUrls) return elements;

    const imageUrlsMap = new Map<string, string>(Object.entries(imageUrls).map(([key, value]) => [key, value]));
    setImageUrls(imageUrlsMap);
  }

  const onSendImages = async () => {
    if (!template) return;

    const imageElements = template.elements.filter((el) => el.type === EEslTemplateElementType.Image && el.imageFile);
    if (!imageElements.length) return template?.elements;

    const images = new Map<number, File>(imageElements.map((el) => [el.id, el.imageFile as File]));
    const formData = new FormData();
    images.forEach((image, key) => {
      formData.append(key.toString(), image);
    });

    const imageKeys = await eslTemplateService.UploadTemplateImages(formData)
    const imageKeysMap = new Map<number, string>(Object.entries(imageKeys).map(([key, value]) => [Number(key), value]));
    if (!imageKeysMap) return template?.elements;

    imageKeysMap.forEach((imageKey, key) => {
      const element = template?.elements.find((el) => el.id === key);
      if (!element) return;

      element.imageSrc = imageKey;
    });

    return template.elements;
  }

  const onSaveTemplate = async () => {
    try {
      if (!template) return;
      if (template.elements.some(el => el.type === EEslTemplateElementType.Image && !el.imageSrc && !el.imageFile)) {
        setIsErrorDisplayed(true);
        return;
      }
      if (template.buildings.length <= 0) {
        setIsErrorDisplayed(true);
        return;
      }

      setIsLoading(true);

      const newElementsWithImages = await onSendImages();
      if (!newElementsWithImages) return;

      const updatedTemplate: IEslTemplate = {
        ...template,
        elements: ceilTemplateItemsValues(newElementsWithImages),
      };

      await eslTemplateService.UpdateEslTemplate(updatedTemplate);
      navigate(0);
    } catch (error) {
      setIsErrorDisplayed(true);
    }
    finally {
      setIsLoading(false);
    }
  }

  const ceilTemplateItemsValues = (elements: IEslTemplateItem[]) => {
    return elements.map((element) => {
      element.dimensions.width = Math.ceil(element.dimensions.width);
      element.dimensions.height = Math.ceil(element.dimensions.height);
      element.position.left = Math.ceil(element.position.left);
      element.position.top = Math.ceil(element.position.top);
      element.style.fontSize = element.style.fontSize ? Math.ceil(element.style.fontSize) : undefined;

      return element;
    });
  }

  const onCancelChanges = () => {
    if (!template) return;
    setTemplate(cachedTemplate);
    setIsErrorDisplayed(false);
    setSelectedElementId(0);
  }

  const doesTemplateHaveChanges = () => {
    if (!template) return false;

    return JSON.stringify(template) !== JSON.stringify(cachedTemplate);
  }

  useEffect(() => {
    if (!template) return;
    if (!doesTemplateHaveChanges()) return setIsTemplateModified(false);

    setIsTemplateModified(true);
    setIsErrorDisplayed(false);
  }, [template]);

  return (
    <Progress overlay isLoading={isLoading}>
      <EslPreviewModeProvider>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 9 }}>
            <Toolbar sx={{ justifyContent: 'space-between' }}>
              <EslPreviewModeToggle />
              <Box>
                <EditOnly>
                  <IconButton onClick={onCancelChanges} disabled={!isTemplateModified}><CancelIcon /></IconButton>
                  <IconButton color="primary" onClick={onSaveTemplate} disabled={!isTemplateModified}><SaveIcon /></IconButton>
                </EditOnly>
              </Box>
            </Toolbar>

            {isErrorDisplayed &&
              <Alert severity="error" sx={{ width: "100%", marginBottom: 4 }}>
                {t("eslTemplate:templateEditor.eslTemplateUpdateError")}
              </Alert>
            }

            {template && (
              <Canvas imageUrls={imageUrls} template={template} setTemplate={setTemplate} selectedElementId={selectedElementId} setSelectedElementId={setSelectedElementId} width={template.width} height={template.height} />
            )}
          </Grid>
          {template && (
            <Grid size={{ xs: 12, md: 3 }}>
              <Sidebar selectedElementId={selectedElementId} setSelectedElementId={setSelectedElementId} template={template} setTemplate={setTemplate} />
            </Grid>
          )}
        </Grid >
      </EslPreviewModeProvider>
    </Progress>
  );
};

export default TemplateEditor;
